<template>
  <div
    class="h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900 flex items-center justify-center p-4 overflow-hidden">
    <!-- Background decoration -->
    <div class="absolute inset-0 overflow-hidden">
      <div
        class="absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"
        style="background-color: #667788;">
      </div>
      <div
        class="absolute -bottom-40 -left-40 w-80 h-80 bg-slate-600 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse">
      </div>
    </div>

    <div class="relative w-full max-w-md" :class="{ 'animate-fade-in': isLoaded }">
      <!-- Logo section with square.png -->
      <div class="text-center mb-10" :class="{ 'animate-slide-up': isLoaded }" style="animation-delay: 0.2s;">
        <div class="relative inline-block cursor-pointer group" :class="{ 'animate-logo-bounce': isLoaded }"
          style="animation-delay: 0.1s;" @click="openTelegram">
          <img src="/square.png" alt="Bondhub"
            class="w-28 h-28 mx-auto mb-4 rounded-2xl shadow-2xl ring-4 ring-white/20 hover:ring-white/40 transition-all duration-300 transform hover:scale-110 group-hover:opacity-20" />
          <div
            class="absolute -inset-1 rounded-2xl blur opacity-25 group-hover:opacity-0 transition-opacity duration-300"
            style="background: linear-gradient(to right, #667788, #5a6b7a);"></div>

          <!-- 항상 보이는 텔레그램 힌트 -->
          <div
            class="absolute -bottom-1 -right-1 w-8 h-8 bg-[#667788] rounded-full flex items-center justify-center shadow-lg animate-pulse">
            <i class="fab fa-telegram-plane text-white text-sm"></i>
          </div>

          <!-- 호버 시 텔레그램 오버레이 (로고 영역에만) -->
          <div
            class="absolute top-0 left-0 w-28 h-28 flex items-center justify-center opacity-0 group-hover:opacity-95 transition-all duration-300 bg-gradient-to-br from-[#667788]/90 to-[#667788]/95 rounded-2xl backdrop-blur-sm">
            <div class="text-center">
              <i class="fab fa-telegram-plane text-white text-3xl mb-1 animate-bounce"></i>
              <p class="text-white text-xs font-medium">텔레그램 연결</p>
            </div>
          </div>
        </div>
        <h1 class="text-3xl font-bold text-white mb-2 tracking-wide">
          <span class="inline-block">{{ displayedTitle }}</span>
        </h1>
        <p class="text-slate-300 text-sm font-medium" :class="{ 'animate-slide-up': isLoaded }"
          style="animation-delay: 2s;">브로커 권혁천</p>
      </div>

      <!-- Login form -->
      <div class="backdrop-blur-xl bg-white/10 p-8 rounded-3xl shadow-2xl border border-white/20"
        :class="{ 'animate-slide-up': isLoaded }" style="animation-delay: 0.6s;">
        <form @submit.prevent="handleLogin" class="space-y-6">
          <div class="relative" :class="{ 'animate-slide-up': isLoaded }" style="animation-delay: 0.8s;">
            <label for="username" class="block text-sm font-semibold text-slate-200 mb-2">
              <i class="fas fa-user mr-2"></i>아이디
            </label>
            <input id="username" v-model="username" type="text"
              class="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-slate-300 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-300 hover:bg-white/15"
              style="--tw-ring-color: #667788;" placeholder="아이디를 입력하세요" required />
          </div>

          <div class="relative" :class="{ 'animate-slide-up': isLoaded }" style="animation-delay: 1.0s;">
            <label for="password" class="block text-sm font-semibold text-slate-200 mb-2">
              <i class="fas fa-lock mr-2"></i>비밀번호
            </label>
            <input id="password" v-model="password" type="password"
              class="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-slate-300 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-300 hover:bg-white/15"
              style="--tw-ring-color: #667788;" placeholder="비밀번호를 입력하세요" required />
          </div>

          <button type="submit"
            class="w-full text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-opacity-50"
            :class="{ 'animate-slide-up': isLoaded }" :style="{
              background: 'linear-gradient(to right, #667788, #5a6b7a)',
              '--tw-ring-color': '#667788',
              'animation-delay': '1.2s'
            }" @mouseover="$event.target.style.background = 'linear-gradient(to right, #5a6b7a, #4d5a68)'"
            @mouseout="$event.target.style.background = 'linear-gradient(to right, #667788, #5a6b7a)'">
            <i class="fas fa-sign-in-alt mr-2"></i>로그인
          </button>
        </form>

        <p v-if="error"
          class="mt-6 text-center text-red-300 bg-red-500/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-red-400/30 animate-shake">
          <i class="fas fa-exclamation-triangle mr-2"></i>{{ error }}
        </p>
      </div>

      <!-- Footer -->
      <div class="text-center mt-8" :class="{ 'animate-fade-in': isLoaded }" style="animation-delay: 1.4s;">
        <p class="text-slate-400 text-xs">
          © 2024 Bondhub. All rights reserved.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

export default {
  name: 'LoginPage',
  setup() {
    const store = useStore();
    const router = useRouter();
    const username = ref('');
    const password = ref('');
    const error = ref('');
    const displayedTitle = ref('');
    const isLoaded = ref(false);

    const typeTitle = () => {
      const fullTitle = 'Bondhub';
      let currentIndex = 0;

      const typeInterval = setInterval(() => {
        if (currentIndex < fullTitle.length) {
          displayedTitle.value = fullTitle.substring(0, currentIndex + 1);
          currentIndex++;
        } else {
          clearInterval(typeInterval);
        }
      }, 150);
    };

    const checkAuth = () => {
      if (store.getters['auth/isAuthenticated']) {
        if (store.getters['auth/isBasic']) {
          // router.push('/login');
        } else {
          router.push('/');
        }
      }
    };

    const handleLogin = async () => {
      try {
        await store.dispatch('auth/login', {
          username: username.value,
          password: password.value,
        });
      } catch (err) {
        console.error('로그인 실패:', err);
        error.value =
          '로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.';
      }
    };

    const openTelegram = () => {
      window.open('https://t.me/+821057110179', '_blank');
    };

    onMounted(() => {
      checkAuth();

      // 페이지 로드 애니메이션 시작
      setTimeout(() => {
        isLoaded.value = true;
      }, 100);

      // 타이핑 애니메이션 시작
      setTimeout(() => {
        typeTitle();
      }, 800);
    });

    return {
      username,
      password,
      error,
      displayedTitle,
      isLoaded,
      handleLogin,
      openTelegram,
    };
  },
};
</script>

<style scoped>
/* 페이드인 애니메이션 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* 슬라이드 업 애니메이션 */
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 로고 바운스 애니메이션 */
@keyframes logoBounce {
  0% {
    opacity: 0;
    transform: scale(0.3) translateY(-50px);
  }

  50% {
    opacity: 0.8;
    transform: scale(1.1) translateY(-10px);
  }

  70% {
    transform: scale(0.95) translateY(5px);
  }

  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* 에러 메시지 흔들림 애니메이션 */
@keyframes shake {

  0%,
  100% {
    transform: translateX(0);
  }

  25% {
    transform: translateX(-5px);
  }

  75% {
    transform: translateX(5px);
  }
}

/* 애니메이션 클래스 */
.animate-fade-in {
  animation: fadeIn 0.8s ease-out forwards;
  opacity: 0;
}

.animate-slide-up {
  animation: slideUp 0.6s ease-out forwards;
  opacity: 0;
  transform: translateY(30px);
}

.animate-logo-bounce {
  animation: logoBounce 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
  opacity: 0;
  transform: scale(0.3) translateY(-50px);
}

.animate-shake {
  animation: shake 0.5s ease-in-out;
}

/* 초기 상태 설정 */
.relative.w-full.max-w-md:not(.animate-fade-in) {
  opacity: 0;
}

.text-center.mb-10:not(.animate-slide-up) {
  opacity: 0;
  transform: translateY(30px);
}

.backdrop-blur-xl:not(.animate-slide-up) {
  opacity: 0;
  transform: translateY(30px);
}

.relative:not(.animate-slide-up) {
  opacity: 0;
  transform: translateY(30px);
}

.text-center.mt-8:not(.animate-fade-in) {
  opacity: 0;
}
</style>
